import type { Movimentacao } from './movimentacao.types';

export type NivelAlertaMovimentacao = 'neutro' | 'atencao' | 'urgente' | 'sucesso' | 'cancelado';

export interface AlertaMovimentacao {
  emoji: string;
  texto: string;
  nivel: NivelAlertaMovimentacao;
}

export const CLASSE_COR_POR_NIVEL_ALERTA: Record<NivelAlertaMovimentacao, string> = {
  neutro: 'bg-gray-100 text-gray-700',
  atencao: 'bg-orange-100 text-orange-800',
  urgente: 'bg-red-100 text-red-800',
  sucesso: 'bg-green-100 text-green-800',
  cancelado: 'bg-gray-100 text-gray-500',
};

/** Mesma família de cor de CLASSE_COR_POR_NIVEL_ALERTA, como borda (uso: destaque lateral
 * de card, ex. Kanban) em vez de fundo do badge. */
export const CLASSE_BORDA_POR_NIVEL_ALERTA: Record<NivelAlertaMovimentacao, string> = {
  neutro: 'border-l-gray-300',
  atencao: 'border-l-orange-400',
  urgente: 'border-l-red-500',
  sucesso: 'border-l-green-400',
  cancelado: 'border-l-gray-300',
};

/** Envio: prazos contados a partir da dataPrevista (Discovery — fórmula da planilha). */
const DIAS_URGENTE_PREPARACAO_ENVIO = 2;
const DIAS_PREPARAR_EQUIPAMENTO_ENVIO = 5;
/** Devolução: quantos dias antes do prazo já cobra o colaborador por um código/método. */
const DIAS_GERAR_CODIGO_DEVOLUCAO = 5;
/** Tolerância antes de virar "Cobrar X" (atrasado sem sinal de movimento). */
const DIAS_TOLERANCIA_COBRANCA = 2;

function paraData(timestamp: Movimentacao['dataPrevista']): Date | null {
  if (!timestamp) return null;
  return 'toDate' in timestamp ? timestamp.toDate() : new Date(timestamp as unknown as string);
}

/** Dias entre hoje e `data` (negativo = atrasado), zerando a hora pra não errar por fuso/horário. */
function diasAte(data: Date): number {
  const hoje = new Date();
  const inicioHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  const inicioData = new Date(data.getFullYear(), data.getMonth(), data.getDate());
  return Math.round((inicioData.getTime() - inicioHoje.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Badge de alerta (emoji + texto + nível de urgência) — decora visualmente o
 * `statusMovimentacao` (fonte da verdade, escolhido manualmente pelo SI) com o texto certo
 * pro método/fluxo (ex. "Aguardando Retirada" só faz sentido pra envio+pessoalmente,
 * "Aguardando Entrega" pra devolução+pessoalmente) e a urgência baseada na proximidade/
 * atraso da `dataPrevista`. NÃO substitui `statusMovimentacao` nem cria um 2º campo pra
 * SI escolher — o método de entrega já basta pra derivar o texto certo (decisão de
 * produto: evita duplicar a mesma informação em dois campos que podem ficar
 * dessincronizados). Adaptado da fórmula de alerta da planilha anterior (Discovery).
 */
export function calcularAlertaMovimentacao(movimentacao: Movimentacao): AlertaMovimentacao {
  const isEnvio = movimentacao.tipoFluxo === 'envio';

  if (movimentacao.statusMovimentacao === 'cancelada') {
    return { emoji: '⛔', texto: 'Cancelada', nivel: 'cancelado' };
  }
  // dataRecebimento pode ter sido registrada pelo colaborador-alvo antes do SI atualizar
  // o status manual — tratamos como concluída de qualquer forma (ver MovimentacaoLogisticaSection).
  if (movimentacao.statusMovimentacao === 'entregue' || movimentacao.dataRecebimento) {
    return { emoji: '✅', texto: isEnvio ? 'Entregue' : 'Devolvido', nivel: 'sucesso' };
  }
  if (movimentacao.statusMovimentacao === 'em_transito') {
    return { emoji: '🚚', texto: 'Em Trânsito', nivel: 'neutro' };
  }
  if (movimentacao.statusMovimentacao === 'aguardando_retirada_correios') {
    return { emoji: '🏤', texto: 'Aguardando Retirada na Agência', nivel: 'atencao' };
  }
  // dataEnvio já registrada (pelo colaborador ou pelo SI) conta como em trânsito
  // independente do status manual ainda estar em 'aguardando_preparacao' — mesmo
  // raciocínio já aplicado mais abaixo pra 'aguardando_postagem_retirada', mas que
  // faltava aqui: sem isso, o alerta continuava pedindo pra postar/preparar um item que
  // já tinha sido enviado de verdade.
  if (movimentacao.dataEnvio) {
    return { emoji: '🚚', texto: 'Em Trânsito', nivel: 'neutro' };
  }

  const dataPrevista = paraData(movimentacao.dataPrevista);
  if (!dataPrevista) {
    return { emoji: '📅', texto: 'Aguardando Data', nivel: 'neutro' };
  }
  const dias = diasAte(dataPrevista);

  if (movimentacao.statusMovimentacao === 'aguardando_preparacao') {
    if (isEnvio) {
      if (dias <= DIAS_URGENTE_PREPARACAO_ENVIO) {
        // "Postagem" só existe nos Correios — Uber entrega, pessoalmente o colaborador retira.
        if (movimentacao.metodoEntrega === 'uber') return { emoji: '🚨', texto: 'Entrega Urgente', nivel: 'urgente' };
        if (movimentacao.metodoEntrega === 'pessoalmente') {
          return { emoji: '🚨', texto: 'Retirada Urgente', nivel: 'urgente' };
        }
        return { emoji: '🚨', texto: 'Postagem Urgente', nivel: 'urgente' };
      }
      if (dias <= DIAS_PREPARAR_EQUIPAMENTO_ENVIO) {
        return { emoji: '🛠️', texto: 'Preparar Equipamento', nivel: 'atencao' };
      }
      return { emoji: '🛠️', texto: 'Preparando Equipamento', nivel: 'neutro' };
    }
    if (dias <= DIAS_GERAR_CODIGO_DEVOLUCAO) {
      // Uber/pessoalmente não passam por Correios — não existe código de postagem pra
      // gerar, o que falta é o equipamento ser entregue de volta.
      if (movimentacao.metodoEntrega === 'uber') return { emoji: '🚗', texto: 'Aguardando Entrega', nivel: 'atencao' };
      if (movimentacao.metodoEntrega === 'pessoalmente') {
        return { emoji: '🤝', texto: 'Aguardando Entrega', nivel: 'atencao' };
      }
      // Código já cadastrado (ver MovimentacaoLogisticaSection) — só falta o colaborador
      // postar de fato, não faz sentido continuar pedindo pra "gerar" um código que já existe.
      if (movimentacao.codigoRastreio) return { emoji: '📮', texto: 'Aguardando Postagem', nivel: 'atencao' };
      return { emoji: '📦', texto: 'Gerar Código de Postagem', nivel: 'atencao' };
    }
    return { emoji: '🛠️', texto: 'Aguardando Preparação', nivel: 'neutro' };
  }

  // A partir daqui só sobra 'aguardando_postagem_retirada' sem dataEnvio ainda (os outros
  // status e o caso com dataEnvio já foram tratados acima).
  const atrasado = dias < -DIAS_TOLERANCIA_COBRANCA;

  if (!movimentacao.metodoEntrega) {
    return { emoji: '📅', texto: 'Aguardando Definir Envio', nivel: 'neutro' };
  }

  switch (movimentacao.metodoEntrega) {
    case 'uber':
      return atrasado
        ? { emoji: '🚨', texto: 'Cobrar Entrega', nivel: 'urgente' }
        : { emoji: '🚗', texto: 'Entrega Programada', nivel: 'atencao' };
    case 'pessoalmente': {
      const acao = isEnvio ? 'Retirada' : 'Entrega';
      return atrasado
        ? { emoji: '🚨', texto: `Cobrar ${acao}`, nivel: 'urgente' }
        : { emoji: '🤝', texto: `Aguardando ${acao}`, nivel: 'atencao' };
    }
    case 'correios':
      return atrasado
        ? { emoji: '🚨', texto: 'Cobrar Postagem', nivel: 'urgente' }
        : { emoji: '📮', texto: 'Aguardando Postagem', nivel: 'atencao' };
    case 'outro':
    default:
      return atrasado
        ? { emoji: '🚨', texto: 'Cobrar Envio', nivel: 'urgente' }
        : { emoji: '📦', texto: 'Aguardando Envio', nivel: 'atencao' };
  }
}
